import { PriceChange } from '@toggle/toggle';
import { addDays, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useMemo } from 'react';

import { getMarketHours } from '~/components/price-display-view/helpers/price-display-helpers';
import { useMarketReactionDates } from '~/modules/market-reaction-charts/hooks/useMarketReactionDates';
import { usePriceChangeHistory } from '~/react-query-hooks/prices/usePriceChangeHistory';

export const NEW_YORK_TIMEZONE = 'America/New_York';
const DATE_FORMAT = 'yyyy-MM-dd';

interface UseMarketReactionDataProps {
  ticker: string;
  reportingDate: string;
  enabled?: boolean;
}

export type PriceChangeByDay = Record<string, PriceChange>;

export const useMarketReactionData = ({
  ticker,
  reportingDate,
  enabled = true,
}: UseMarketReactionDataProps) => {
  const reportingDateET = toZonedTime(
    new Date(reportingDate),
    NEW_YORK_TIMEZONE
  );
  const { isPreMarket: isReportingPreMarket } = getMarketHours(
    new Date(reportingDate)
  );
  const { fromDateUTC, toDateUTC } = useMarketReactionDates(reportingDate);

  const { data: priceChangeData } = usePriceChangeHistory({
    ticker,
    reference: fromDateUTC.toISOString(),
    from: fromDateUTC.toISOString(),
    to: toDateUTC.toISOString(),
    enabled: enabled,
  });

  const priceChangeByDay = useMemo(
    () =>
      priceChangeData?.reduce<PriceChangeByDay>((acc, point) => {
        const zonedDate = toZonedTime(new Date(point.time), NEW_YORK_TIMEZONE);
        const day = format(zonedDate, DATE_FORMAT);

        if (!acc[day]) {
          acc[day] = [];
        }

        acc[day].push(point);

        return acc;
      }, {}),
    [priceChangeData]
  );

  if (!priceChangeByDay || Object.keys(priceChangeByDay).length === 0) {
    return {
      priceChangeData: undefined,
      fromDateEST: undefined,
      toDateEST: undefined,
    };
  }

  const days = Object.keys(priceChangeByDay);
  // Previous day for pre-market insights
  const theoreticalFromDate = isReportingPreMarket
    ? addDays(reportingDateET, -1)
    : reportingDateET;
  const theoreticalFromDateStr = format(theoreticalFromDate, DATE_FORMAT);
  const directIndex = days.indexOf(theoreticalFromDateStr);
  // Fallback on previous day with data to handle weekends or holidays
  const fromIndex =
    directIndex >= 0
      ? directIndex
      : Math.max(
          days.findLastIndex(day => day <= theoreticalFromDateStr),
          0
        );

  const fromPriceChangeData = (priceChangeByDay[days[fromIndex]] ?? []).filter(
    point => {
      const zonedTime = toZonedTime(new Date(point.time), NEW_YORK_TIMEZONE);
      const hours = zonedTime.getHours();
      const minutes = zonedTime.getMinutes();
      return hours > 9 || (hours === 9 && minutes >= 30);
    }
  );

  const toDay = days[fromIndex + 1];
  const toPriceChangeData = toDay
    ? priceChangeByDay[toDay].filter(point => {
        const zonedTime = toZonedTime(new Date(point.time), NEW_YORK_TIMEZONE);
        const hours = zonedTime.getHours();
        const minutes = zonedTime.getMinutes();
        return hours < 16 || (hours === 16 && minutes === 0);
      })
    : [];

  const fallbackToDate = addDays(`${days[fromIndex]}T16:00:00`, 1);
  return {
    priceChangeData: [...fromPriceChangeData, ...toPriceChangeData],
    fromDateEST: new Date(`${days[fromIndex]}T09:30:00`),
    toDateEST: toDay ? new Date(`${toDay}T16:00:00`) : fallbackToDate,
  };
};
